import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import styles from './RichTextBubbleFontFamilyPicker.module.css';

/**
 * Sélecteur de police du bubble menu — dropdown maison, sans portail.
 *
 * Remplace un Mantine `Select` (Combobox + portail vers document.body) qui
 * ne fonctionnait jamais dans ce contexte : le bubble menu Tiptap est
 * lui-même un portail (`createPortal` géré par le plugin), et le dropdown
 * du Select se portait SÉPARÉMENT ailleurs — un clic sur une option était
 * vu comme un clic « à l'extérieur » du bubble menu, qui se fermait/se
 * démontait avant que `onChange` ne s'exécute (confirmé : `onChange`
 * n'était jamais appelé, aucune erreur). Ici la liste est un simple enfant
 * React normal, rendu dans le même DOM que le reste du bubble menu — même
 * mécanique (mousedown + preventDefault, pas de focus perdu) que le picker
 * de couleur, qui lui fonctionne.
 */
export default function RichTextBubbleFontFamilyPicker({
  value,
  options,
  onChange,
  rememberNow,
  lockBubbleMenu,
  unlockBubbleMenu,
  onRestoreSelection,
  /* Le même dropdown sert à la taille : sans recherche, sans aperçu de police,
     et avec un libellé propre à la valeur courante (« 18 px » n'est pas
     forcément dans la liste). */
  searchable = true,
  previewFont = true,
  clearable = true,
  clearLabel = 'Aucune (par défaut)',
  placeholder = 'Police',
  triggerText,
  triggerClassName,
  /* Première entrée, choisie quand `value` est vide : le texte suit alors la
     police héritée (`font`), dont l'entrée porte le nom. Remplace `clearable`. */
  defaultOption = null,
  listLabel = null,
}) {
  const [open, setOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value) || null;

  const close = () => {
    setOpen(false);
    setFilter('');
    unlockBubbleMenu?.();
    window.requestAnimationFrame(() => onRestoreSelection?.());
  };

  const openMenu = () => {
    rememberNow?.();
    lockBubbleMenu?.();
    setFilter('');
    // Pas de portail (cf. commentaire plus haut) : le dropdown s'ouvre
    // toujours "vers le bas" sans ça — coupé par le bord du canvas
    // scrollable quand le bloc édité est proche du bas de page (ex. footer).
    const rect = wrapRef.current?.getBoundingClientRect();
    const DROPDOWN_MAX_HEIGHT = 260;
    setOpenAbove(!!rect && window.innerHeight - rect.bottom < DROPDOWN_MAX_HEIGHT + 8);
    setOpen(true);
    if (searchable) window.requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const handleTriggerMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) close();
    else openMenu();
  };

  const handleOptionMouseDown = (optValue) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    rememberNow?.();
    onChange(optValue);
    close();
  };

  const handleClearMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    rememberNow?.();
    onChange(null);
    close();
  };

  const handleSearchMouseDown = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      // Le BubbleMenu dispatch un mousedown SYNTHÉTIQUE depuis sa racine
      // (contournement preventHide Tiptap) juste avant le vrai clic : vu
      // comme « extérieur », il fermait le dropdown avant que le clic
      // n'atteigne l'option. Les événements non-fiables sont ignorés.
      if (!e.isTrusted) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    // Document de l'éditeur (iframe sur le canvas — `document` parent n'y
    // voyait jamais les clics : le clic-extérieur n'y fermait jamais rien).
    const doc = wrapRef.current?.ownerDocument || document;
    doc.addEventListener('mousedown', handleOutside, { capture: true });
    return () => doc.removeEventListener('mousedown', handleOutside, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = filter
    ? options.filter((opt) => opt.label.toLowerCase().includes(filter.toLowerCase()))
    : options;

  const triggerFont = previewFont ? (value || defaultOption?.font) : null;
  // Une police posée hors de la liste (hors thème) reste affichée.
  const triggerLabel = triggerText || selectedOption?.label || value || defaultOption?.label || placeholder;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.trigger} ${triggerClassName || ''}`}
        onMouseDown={handleTriggerMouseDown}
      >
        <span
          className={`${styles.triggerLabel} ${triggerLabel === placeholder ? styles.triggerPlaceholder : ''}`}
          style={triggerFont ? { fontFamily: `${triggerFont}, sans-serif` } : undefined}
        >
          {triggerLabel}
        </span>
        <FontAwesomeIcon icon={icon({ name: 'chevron-down', style: 'solid' })} style={{ fontSize: 14 }} />
      </button>
      {open && (
        <div
          className={`${styles.dropdown} ${openAbove ? styles.dropdownAbove : ''}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {searchable && (
            <input
              ref={searchInputRef}
              type="text"
              className={styles.search}
              placeholder="Rechercher..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onMouseDown={handleSearchMouseDown}
            />
          )}
          <div className={styles.list}>
            {defaultOption && !filter && (
              <button
                type="button"
                className={`${styles.option} ${!value ? styles.optionSelected : ''}`}
                style={defaultOption.font ? { fontFamily: `${defaultOption.font}, sans-serif` } : undefined}
                onMouseDown={handleClearMouseDown}
              >
                {defaultOption.label}
              </button>
            )}
            {listLabel && filtered.length > 0 && (
              <div className={styles.listLabel}>{listLabel}</div>
            )}
            {!defaultOption && clearable && value && (
              <button
                type="button"
                className={styles.option}
                onMouseDown={handleClearMouseDown}
              >
                {clearLabel}
              </button>
            )}
            {filtered.length === 0 && (
              <div className={styles.empty}>Aucune police trouvée</div>
            )}
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.option} ${opt.value === value ? styles.optionSelected : ''}`}
                style={previewFont ? { fontFamily: `${opt.value}, sans-serif` } : undefined}
                onMouseDown={handleOptionMouseDown(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
